import { useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  Home,
  MyLocation,
  ContentCopy,
  Check,
  Close,
  EditLocation,
  Refresh,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { RootState, AppDispatch } from '../../store/store';
import {
  startPlacement,
  cancelPlacement,
  setCruiseSpeedMs,
  clearHomeBase,
  fetchHomeBase,
  setHomeBase,
  fetchShortestPath,
} from '../../store/slices/homeBaseSlice';
import { formatLatLng, formatDistance, formatDuration, formatTimestamp } from '../../utils/formatters';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CRUISE_SPEED_OPTIONS = [5, 8, 10, 12, 15, 20] as const;
const DEFAULT_ALTITUDE_M = 0;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Copy-to-clipboard button with transient confirmation tick. */
const CopyButton: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label}`}>
      <IconButton
        onClick={handleCopy}
        size="small"
        aria-label={copied ? 'Copied to clipboard' : `Copy ${label} to clipboard`}
        sx={{ ml: 0.5 }}
      >
        {copied ? (
          <Check fontSize="inherit" color="success" />
        ) : (
          <ContentCopy fontSize="inherit" />
        )}
      </IconButton>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

/**
 * HomeBasePanel — shows home base coordinates, shortest-path stats, and
 * placement controls for the currently selected drone.
 *
 * WS INTEGRATION POINT: When the backend adds a WebSocket gateway for
 * home-base updates (e.g. "home_base_updated" event), subscribe here (or
 * in a dedicated useHomeBaseWebSocket hook) and dispatch fetchHomeBase /
 * fetchShortestPath on receipt. For now data is fetched on drone selection
 * and refreshed on explicit user request.
 */
export const HomeBasePanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const selectedDroneId = useSelector(
    (state: RootState) => state.drones.selectedDroneId
  );
  const {
    homeBase,
    shortestPath,
    placementMode,
    pendingPin,
    cruiseSpeedMs,
    loading,
    settingHomeBase,
    loadingPath,
    error,
    pathError,
  } = useSelector((state: RootState) => state.homeBase);

  // Track the last droneId we loaded for — clear state on change.
  const loadedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedDroneId) {
      dispatch(clearHomeBase());
      loadedForRef.current = null;
      return;
    }

    if (loadedForRef.current !== selectedDroneId) {
      dispatch(clearHomeBase());
      loadedForRef.current = selectedDroneId;
      dispatch(fetchHomeBase(selectedDroneId));
    }
  }, [selectedDroneId, dispatch]);

  // Fetch shortest path once homeBase arrives (and is set) for the selected drone.
  useEffect(() => {
    if (
      selectedDroneId &&
      homeBase &&
      homeBase.is_set &&
      !shortestPath &&
      !loadingPath &&
      !pathError
    ) {
      dispatch(fetchShortestPath({ droneId: selectedDroneId, cruiseSpeedMs }));
    }
  }, [
    selectedDroneId,
    homeBase,
    shortestPath,
    loadingPath,
    pathError,
    cruiseSpeedMs,
    dispatch,
  ]);

  // Escape key cancels placement.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && placementMode === 'placing') {
        dispatch(cancelPlacement());
      }
    },
    [placementMode, dispatch]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // ----- Handlers -----

  const handleStartPlacement = () => {
    dispatch(startPlacement());
  };

  const handleCancelPlacement = () => {
    dispatch(cancelPlacement());
  };

  const handleConfirmPin = () => {
    if (!selectedDroneId || !pendingPin) return;
    dispatch(
      setHomeBase({
        droneId: selectedDroneId,
        request: {
          latitude: pendingPin.lat,
          longitude: pendingPin.lng,
          altitude: DEFAULT_ALTITUDE_M,
        },
      })
    ).then(() => {
      // Refetch shortest path after setting the home base.
      dispatch(
        fetchShortestPath({ droneId: selectedDroneId, cruiseSpeedMs })
      );
    });
  };

  const handleRefreshPath = () => {
    if (!selectedDroneId) return;
    dispatch(fetchShortestPath({ droneId: selectedDroneId, cruiseSpeedMs }));
  };

  const handleCruiseSpeedChange = (e: SelectChangeEvent<number>) => {
    const speed = Number(e.target.value);
    dispatch(setCruiseSpeedMs(speed));
    if (selectedDroneId && homeBase?.is_set) {
      dispatch(fetchShortestPath({ droneId: selectedDroneId, cruiseSpeedMs: speed }));
    }
  };

  // ----- Render nothing when no drone is selected -----
  if (!selectedDroneId) {
    return null;
  }

  const coordString =
    homeBase && homeBase.is_set
      ? formatLatLng(homeBase.latitude, homeBase.longitude)
      : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Home sx={{ color: '#0066FF', fontSize: 20 }} aria-hidden="true" />
        <Typography variant="subtitle2" fontWeight="bold">
          Home Base
        </Typography>
        {loading && (
          <CircularProgress
            size={14}
            aria-label="Loading home base"
            sx={{ ml: 'auto' }}
          />
        )}
      </Box>

      {/* Error state */}
      {error && (
        <Alert severity="error" role="alert" sx={{ py: 0.5 }}>
          {humaniseError(error)}
        </Alert>
      )}

      {/* Loading state (first load, no home base data yet) */}
      {loading && !homeBase && !error && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          aria-live="polite"
        >
          <Typography variant="body2" color="text.secondary">
            Loading home base&hellip;
          </Typography>
        </Box>
      )}

      {/* Empty state: no home base set and not in placement mode */}
      {!loading &&
        !error &&
        homeBase &&
        !homeBase.is_set &&
        placementMode === 'idle' &&
        !pendingPin && (
          <Box aria-live="polite">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No home base set for this drone.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MyLocation />}
              onClick={handleStartPlacement}
              aria-label="Set home base — click the map to choose a location"
            >
              Set Home Base
            </Button>
          </Box>
        )}

      {/* Placement mode: awaiting map click */}
      {placementMode === 'placing' && !pendingPin && (
        <Box aria-live="polite">
          <Typography
            variant="body2"
            color="primary"
            sx={{ mb: 1, fontWeight: 'medium' }}
          >
            Click anywhere on the map to place the home base.
          </Typography>
          <Button
            variant="text"
            size="small"
            startIcon={<Close />}
            onClick={handleCancelPlacement}
            aria-label="Cancel home base placement"
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Pending pin: operator clicked the map, awaiting confirmation */}
      {pendingPin && (
        <Box aria-live="polite">
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Confirm placing home base at:
          </Typography>
          <Typography
            variant="body2"
            fontFamily="monospace"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            {formatLatLng(pendingPin.lat, pendingPin.lng)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={settingHomeBase}
              onClick={handleConfirmPin}
              aria-label="Confirm home base placement"
              startIcon={
                settingHomeBase ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Check />
                )
              }
            >
              {settingHomeBase ? 'Saving…' : 'Confirm'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={settingHomeBase}
              onClick={handleCancelPlacement}
              aria-label="Cancel home base placement"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* Populated state: home base is set */}
      {!loading &&
        !error &&
        homeBase &&
        homeBase.is_set &&
        placementMode === 'idle' &&
        !pendingPin && (
          <>
            {/* Coordinates */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Coordinates
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" fontFamily="monospace">
                  {coordString}
                </Typography>
                {coordString && (
                  <CopyButton value={coordString} label="coordinates" />
                )}
              </Box>
              {homeBase.altitude !== 0 && (
                <Typography variant="body2" color="text.secondary">
                  Altitude: {homeBase.altitude.toFixed(1)} m
                </Typography>
              )}
            </Box>

            {/* Last set */}
            {homeBase.set_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Set
                </Typography>
                <Typography variant="body2">
                  {formatTimestamp(homeBase.set_at)}
                  {homeBase.set_by ? ` by ${homeBase.set_by}` : ''}
                </Typography>
              </Box>
            )}

            {/* Edit button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditLocation />}
              onClick={handleStartPlacement}
              aria-label="Edit home base — click the map to choose a new location"
              sx={{ alignSelf: 'flex-start' }}
            >
              Edit
            </Button>

            <Divider />

            {/* Shortest path section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MyLocation sx={{ fontSize: 16, color: 'text.secondary' }} aria-hidden />
              <Typography variant="subtitle2">Return Path</Typography>
              {loadingPath && (
                <CircularProgress
                  size={13}
                  aria-label="Loading shortest path"
                  sx={{ ml: 'auto' }}
                />
              )}
              {!loadingPath && (
                <IconButton
                  size="small"
                  onClick={handleRefreshPath}
                  aria-label="Refresh shortest path"
                  sx={{ ml: 'auto' }}
                >
                  <Refresh fontSize="inherit" />
                </IconButton>
              )}
            </Box>

            {/* Path error */}
            {pathError && (
              <Alert severity="warning" role="alert" sx={{ py: 0.5 }}>
                {humanisePathError(pathError)}
              </Alert>
            )}

            {/* Cruise speed selector */}
            <FormControl size="small" sx={{ maxWidth: 160 }}>
              <InputLabel id="cruise-speed-label">Cruise speed</InputLabel>
              <Select<number>
                labelId="cruise-speed-label"
                value={cruiseSpeedMs}
                label="Cruise speed"
                onChange={handleCruiseSpeedChange}
                aria-describedby="cruise-speed-hint"
              >
                {CRUISE_SPEED_OPTIONS.map((speed) => (
                  <MenuItem key={speed} value={speed}>
                    {speed} m/s
                  </MenuItem>
                ))}
              </Select>
              <Typography
                id="cruise-speed-hint"
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                Used to calculate ETA
              </Typography>
            </FormControl>

            {/* Path stats */}
            {shortestPath && !pathError && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                aria-live="polite"
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Distance
                  </Typography>
                  <Typography variant="body2">
                    {formatDistance(shortestPath.distance_km)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Estimated flight time
                  </Typography>
                  <Typography variant="body2">
                    {formatDuration(shortestPath.estimated_flight_time_sec / 60)}
                  </Typography>
                </Box>
                {shortestPath.routing &&
                  (shortestPath.routing.fallback ? (
                    <Alert severity="warning" role="alert" sx={{ py: 0.5 }}>
                      Routed directly — could not avoid no-fly zones
                      {shortestPath.routing.reason
                        ? ` (${shortestPath.routing.reason})`
                        : ''}
                      .
                    </Alert>
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Routing
                      </Typography>
                      <Typography variant="body2">
                        {shortestPath.routing.avoided_zones > 0
                          ? `Avoids ${shortestPath.routing.avoided_zones} no-fly zone${
                              shortestPath.routing.avoided_zones === 1 ? '' : 's'
                            }`
                          : 'Direct path'}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            )}

            {/* Loading path, no data yet */}
            {loadingPath && !shortestPath && (
              <Typography
                variant="body2"
                color="text.secondary"
                aria-live="polite"
              >
                Calculating path&hellip;
              </Typography>
            )}

            {/* No path and no error — drone position unknown */}
            {!loadingPath && !shortestPath && !pathError && (
              <Typography
                variant="body2"
                color="text.secondary"
                aria-live="polite"
              >
                Path unavailable — drone position unknown.
              </Typography>
            )}
          </>
        )}
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Error message helpers
// ---------------------------------------------------------------------------

/** Convert raw error strings to operator-friendly messages. */
function humaniseError(raw: string): string {
  if (raw.includes('404') || raw.toLowerCase().includes('not found')) {
    return 'Home base not set for this drone.';
  }
  if (raw.includes('403') || raw.toLowerCase().includes('forbidden')) {
    return 'You do not have permission to view this drone\'s home base.';
  }
  return `Failed to load home base: ${raw}`;
}

function humanisePathError(raw: string): string {
  if (raw.toLowerCase().includes('position')) {
    return 'Drone position unknown — cannot calculate return path.';
  }
  if (raw.toLowerCase().includes('home')) {
    return 'Home base not set — cannot calculate return path.';
  }
  if (raw.includes('404') || raw.toLowerCase().includes('not found')) {
    return 'Drone not found on the server.';
  }
  return `Path calculation failed: ${raw}`;
}
